import React, { useMemo, forwardRef } from 'react';

import { Spinner } from '../PageLoader';

type DealButtonProps = {
  label: string;
  children?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  Icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  variant?: 'primary' | 'success' | 'reject' | 'dark' | 'success_secondary';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  /** Shown next to the spinner when `loading` is true (default: Loading...) */
  loadingLabel?: string;
  'data-testid'?: string;
};

const variantClasses = {
  primary: 'text-brand-950 bg-brand-400 hover:text-white hover:bg-brand-800 border-none',
  success: 'text-brand-950 bg-green-400 hover:text-white hover:bg-green-600 border-none',
  success_secondary: 'text-brand-950 bg-green-400 hover:text-white hover:bg-green-600 border-none',
  reject: 'text-red-400 hover:bg-red-400 hover:text-white border-red-400',
  dark: 'text-white bg-grey-950 border-gray-700 hover:bg-background-canvas hover:border-red-500',
};

const spinnerColors = {
  foregroundOnAccent: '#260626',
  errorDefault: '#e95c7b',
  foregroundDefault: '#ffffff',
  foregroundOnDisabled: '#9e9e9e',
  rejectDisabled: '#f2a2b3',
  darkDisabled: '#bdbdbd',
};

const disabledVariantClasses = {
  primary: 'text-gray-500 bg-gray-800 border-none',
  success: 'bg-gray-600 border border-green-600 text-white',
  // Matches Button's secondary_green disabled style while preserving the success cue.
  success_secondary: 'bg-gray-600 border border-green-600 text-white',
  reject: 'text-red-300 bg-transparent border-red-300 opacity-60',
  dark: 'text-gray-400 bg-gray-800 border-gray-600 opacity-60',
};

const DealButton = forwardRef<HTMLButtonElement, DealButtonProps>(function DealButton(
  {
    label,
    children,
    onClick,
    className = '',
    Icon,
    iconPosition = 'left',
    variant = 'primary',
    disabled = false,
    type = 'button',
    loading = false,
    loadingLabel = 'Loading...',
    'data-testid': dataTestId,
  },
  ref,
) {
  const baseClasses = useMemo(
    () =>
      `inline-flex items-center gap-4 py-2.5 px-4 rounded font-heebo text-sm font-medium border
       transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
       ${disabled || loading ? disabledVariantClasses[variant] : variantClasses[variant]}
       ${className}`,
    [variant, className, disabled, loading],
  );

  const disabledClasses = disabled || loading ? 'hover:cursor-not-allowed' : '';

  // Get the text color for the spinner based on variant and state
  const getSpinnerColor = () => {
    if (disabled || loading) {
      switch (variant) {
        case 'success':
        case 'success_secondary':
          return spinnerColors.foregroundDefault;
        case 'reject':
          return spinnerColors.rejectDisabled;
        case 'dark':
          return spinnerColors.darkDisabled;
        default:
          return spinnerColors.foregroundOnDisabled;
      }
    }
    // Not disabled/loading - use normal text colors
    switch (variant) {
      case 'success':
      case 'success_secondary':
        return spinnerColors.foregroundOnAccent;
      case 'reject':
        return spinnerColors.errorDefault;
      case 'dark':
        return spinnerColors.foregroundDefault;
      default:
        return spinnerColors.foregroundOnAccent;
    }
  };

  return (
    <button
      ref={ref}
      aria-label={label}
      data-testid={dataTestId}
      onClick={disabled || loading ? undefined : onClick}
      className={`${baseClasses} ${disabledClasses}`}
      disabled={disabled || loading}
      type={type}
    >
      {loading ? (
        <>
          <Spinner loading={true} size={16} color={getSpinnerColor()} />
          <span>{loadingLabel}</span>
        </>
      ) : (
        <>
          {iconPosition === 'left' && Icon}
          {children}
          {iconPosition === 'right' && Icon}
        </>
      )}
    </button>
  );
});

export default React.memo(DealButton);
